import { SurveySite } from "@fieldbook/common/types/surveySites"
import { TableInsert } from "@fieldbook/common/types/util"
import { useMutation } from "@tanstack/react-query"
import { PhotoInput } from "@/lib/queryClient/PartialSuccessError"
import { persistStagedPhotosForNewEntity } from "./persistStagedPhotosForNewEntity"
import { CREATE_SURVEY_SITE_MUTATION_KEY } from "@/lib/queryClient/mutationKeys"
import { psInsert } from "@/lib/powersync/crud"
import { powerSyncDb } from "@/lib/powersync/db"
import { removeFailedMutationsForEntity } from "@/lib/queryClient/removeFailedMutationsForEntity"

export type CreateSurveySitePayload = TableInsert<"survey_sites"> & {
  id: string
  photos?: PhotoInput[]
}

/**
 * With PowerSync, the entity is written to local SQLite immediately.
 * It's never "unfinished" in the old mutation-cache sense.
 * Kept as an export for observation form consumers (always returns false).
 */
export const getHasUnfinishedCreateSurveySite = (_siteId: string): boolean =>
  false

/**
 * Create a survey site by writing to PowerSync local SQLite.
 * Photo records are also written locally; photo files are uploaded
 * to Supabase Storage. PowerSync syncs all DB records to Supabase.
 */
export async function createSurveySiteFn(
  payload: CreateSurveySitePayload,
): Promise<SurveySite> {
  const { photos: photoInputs, ...entityPayload } = payload
  const now = new Date().toISOString()

  const entityWithTimestamps = {
    ...entityPayload,
    created_at: entityPayload.created_at ?? now,
    updated_at: entityPayload.updated_at ?? now,
  }

  const photoInputsWithFile = (photoInputs ?? []).filter((p) => {
    if (!p.file) {
      console.warn(`[CreateSurveySite] Photo ${p.id} has no file attached`)
      return false
    }
    return true
  })

  // The attachment queue inserts photo rows only after each blob is durable.
  await powerSyncDb.writeTransaction(async (tx) => {
    await psInsert("survey_sites", entityWithTimestamps, tx)
  })

  const entity = entityWithTimestamps as unknown as SurveySite

  const failedPhotos = await persistStagedPhotosForNewEntity({
    photos: photoInputsWithFile,
    entityId: entity.id,
    entityType: "survey_site",
    onError: (photo, error) => {
      console.error(
        `[CreateSurveySite] Failed to persist photo ${photo.id}:`,
        error,
      )
    },
  })
  if (failedPhotos.length > 0) {
    throw new Error("Couldn't save all photos. Please try again.")
  }

  return entity
}

/**
 * Hook for creating a survey site with embedded photos.
 */
export const useCreateSurveySite = () => {
  return useMutation({
    mutationKey: CREATE_SURVEY_SITE_MUTATION_KEY,
    mutationFn: createSurveySiteFn,
    onMutate: (payload) => removeFailedMutationsForEntity(payload.id),
    networkMode: "always",
  })
}
